// Externals imports.
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React, { ReactElement, ReactNode, useContext, useEffect, useState } from 'react'

import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Accordion, AccordionDetails, AccordionSummary, ClickAwayListener } from '@material-ui/core'
import { LanguageMyReportList, LanguagePrompt } from '../../../../i18n'
import { TRUE } from '../../../NonReact/constants/Common'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    handleReportUpdateRenameActiveRptDescription,
    handleRptPortalReportItemSelection
} from '../../../NonReact/utils/RptPortal'
import Checkbox from '../../Common/Components/Checkbox'
import ContextMenu, { ContextMenuPosition } from '../../Common/Components/ContextMenu'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import TextInput from '../../Common/Components/TextInput'
import { MessageContext, PromptContext, ServletPathContext } from '../../Common/Contexts'
import { ReactState, StateSetter } from '../../Common/Types/Types'
import { matchParentFolderId, recursiveSubFolderGet } from '../../Common/Utils/ReportList'
import { renameFolder, renameReport } from './API'
import { FOLDERS_SEPARATOR_BACKEND, ReportDragDropClasses, ReportDragDropIDs } from './Constants'
import PromptMoveToBinMessage from './PromptMoveToBinMessage'
import {
    DraggableFolderData,
    DraggableReportData,
    DroppableData,
    ReportListItemsIndexByReportID,
    ReportListItemType
} from './Types'

/**
 * Component properties type.
 */
type Props = {
    reportListItem: ReportListItemType
    isReportWriteAllowed: boolean
    hoveredItemID: Nullable<string>
    setHoveredItemID: StateSetter<Nullable<string>>
    editingItemID: Nullable<string>
    setEditingItemID: (newValue: Nullable<string>) => void
    checkedItemsIDs: string[]
    setCheckedItemsIDs: StateSetter<string[]>
    reportListItemsAsMap: ReportListItemsIndexByReportID
    setReportListItemsAsMap: StateSetter<ReportListItemsIndexByReportID>
    initialiseReportListData: () => Promise<void>
    setHasHighlight: (value: boolean) => void
    modifyReport?: (e: React.MouseEvent, reportID: string) => void
    copyReport?: (reportID: string) => void
    isParentFolderHighlighted: boolean
    handleReportDelete: (reportID: string) => void
    handleFolderMoveToBin: (folderID: ReportListItemType) => void
    dndDisabled: boolean
}

const NO_REMINDER_VARIABLE = 'displayPromptOnMovingReportToBin'

/**
 * The report list item that could be the folder or the report.
 * The structure is build as a tree based on the report list API.
 *
 * @param reportListItem The report list item - report or folder.
 * @param isReportWriteAllowed Whether the user has write permissions for report entity.
 * @param hoveredItemID The ID of currently hovered item.
 * @param setHoveredItemID Setter for corresponding property.
 * @param editingItemID The ID of currently editing item - we support only one at the same time.
 * @param setEditingItemID The setter for corresponding property.
 * @param checkedItemsIDs Array of checked items IDs.
 * @param setCheckedItemsIDs The setter for corresponding array.
 * @param reportListItemsAsMap Report items as map indexed by ID.
 * @param setReportListItemsAsMap The setter for corresponding report items map.
 * @param initialiseReportListData Report list data initialisation method.
 * @param setHasHighlight Set is there a highlighted row.
 * @param modifyReport This function allows a user to modify the current report.
 * @param copyReport This function allows a user to copy the current report.
 * @param isParentFolderHighlighted Is there a highlighted row.
 * @param handleReportDelete This function allows a user to delete the current report.
 * @param handleFolderMoveToBin This function allows a user to move the current folder to bin.
 */
const ReportListItem: React.FC<Props> = ({
    reportListItem,
    isReportWriteAllowed,
    hoveredItemID,
    setHoveredItemID,
    editingItemID,
    setEditingItemID,
    checkedItemsIDs,
    setCheckedItemsIDs,
    reportListItemsAsMap,
    setReportListItemsAsMap,
    initialiseReportListData,
    setHasHighlight,
    modifyReport,
    copyReport,
    isParentFolderHighlighted,
    handleReportDelete,
    handleFolderMoveToBin,
    dndDisabled
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguagePrompt')
    language.loadBundle('LanguageMyReportList')
    const servletPath = useContext(ServletPathContext) as string
    const { displayError } = useContext(MessageContext)
    const { displayPrompt } = useContext(PromptContext)

    const [isShowContentUntilTransitionFinishes, setIsShowContentUntilTransitionFinishes] = useState<boolean>(false)

    const { id, name, subItems, isScheduleActive, durationFilterSummary, parentFolderID } = reportListItem
    const isHovered = id === hoveredItemID
    const isNameEditing = id === editingItemID
    const isChecked = checkedItemsIDs.includes(id)

    const isFolderItem = !!subItems && subItems.length > 0
    const isFolderOpen = isFolderItem && checkedItemsIDs.includes(reportListItem.id)

    // These are the values we will propagate down to the report list items in case of deeper items hierarchy.
    const propagatedValues = {
        isReportWriteAllowed,
        hoveredItemID,
        setHoveredItemID,
        editingItemID,
        setEditingItemID,
        checkedItemsIDs,
        setCheckedItemsIDs,
        reportListItemsAsMap,
        setReportListItemsAsMap,
        initialiseReportListData,
        setHasHighlight,
        modifyReport,
        copyReport,
        handleReportDelete,
        handleFolderMoveToBin
    }

    const [editedName, setEditedName] = useState<string>('')
    const [isHighlighted, setIsHighlighted]: ReactState<boolean> = useState<boolean>(false)
    const [isFolderHighlighted, setIsFolderHighlighted]: ReactState<boolean> = useState<boolean>(false)

    /**
     * Unchecked folder sub items.
     *
     * @param folderID - ID of the folder to uncheck sub items for.
     * @param reportIDsToUncheck - List of report IDs to uncheck if they are in folders.
     */
    const uncheckFolderSubItems = (folderID: string, reportIDsToUncheck?: string[]): void => {
        // Get a list of all of the report list items.
        const reportList = Array.from(reportListItemsAsMap.values())
        // List of folder IDs.
        const folders: string[] = recursiveSubFolderGet(reportList, [folderID])

        if (reportIDsToUncheck) {
            // Uncheck only provided list of reportIDs within the folders.
            const reportItemIDsToUncheck = reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem) =>
                        matchParentFolderId(currentReportListItem, folders) &&
                        currentReportListItem.isReport &&
                        reportIDsToUncheck.includes(currentReportListItem.id)
                )
                .map((currentReportListItem): string => currentReportListItem.id)

            setCheckedItemsIDs((prevCheckedItemsIDs: string[]) => {
                const newListOfCheckedItemIDs = prevCheckedItemsIDs.filter((prevCheckedItemID): boolean =>
                    reportItemIDsToUncheck.includes(prevCheckedItemID)
                )
                return newListOfCheckedItemIDs
            })
        } else {
            // When folder is collapsed uncheck all selected options.
            reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem) =>
                        matchParentFolderId(currentReportListItem, folders) && currentReportListItem.isReport
                )
                .forEach((currentReportListItem) => {
                    // Uncheck every item
                    setCheckedItemsIDs((prevCheckedItemsIDs) => {
                        if (checkedItemsIDs.includes(currentReportListItem.id)) {
                            return prevCheckedItemsIDs.filter((itemID) => itemID !== currentReportListItem.id)
                        }

                        return prevCheckedItemsIDs
                    })
                })
        }
    }

    /**
     * Folder item click handler.
     * @param event click event.
     * @param rptListItemID The identifier of the menu item.
     */
    const handleReportListFolderClick = (
        event: React.MouseEvent | React.ChangeEvent<unknown> | null,
        rptListItemID: string
    ): void => {
        event?.stopPropagation()

        const folder: ReportListItemType | undefined = reportListItemsAsMap.get(rptListItemID)

        if (folder) {
            setReportListItemsAsMap((pPrevStateReportsAsMap) => {
                setCheckedItemsIDs((prevCheckedItemsIDs) => {
                    if (checkedItemsIDs.includes(folder.id)) {
                        // Folder was already expanded and we have to collapse it.
                        return prevCheckedItemsIDs.filter((itemID) => itemID !== folder.id)
                    }
                    // Folder was collapsed and we have to expand it.
                    return [...prevCheckedItemsIDs, folder.id]
                })

                return new Map(pPrevStateReportsAsMap)
            })

            // Uncheck all sub items.
            uncheckFolderSubItems(rptListItemID)
        }
    }

    /**
     * Report item click handler.
     * @param reportItem The report item.
     */
    const handleReportListReportClick = (reportItem: ReportListItemType): void => {
        handleRptPortalReportItemSelection(reportItem)
    }

    /**
     * Item check handler.
     * @param checked
     * @param rptListItemID The identifier of the menu item.
     */
    const handleReportListCheckboxClick = (checked: boolean, rptListItemID: string): void => {
        setReportListItemsAsMap((pPrevStateReportsAsMap) => {
            const reportItem = pPrevStateReportsAsMap.get(rptListItemID)
            if (reportItem) {
                setCheckedItemsIDs((prevCheckedItemsIDs) => {
                    if (checkedItemsIDs.includes(reportItem.id)) {
                        // Report was already selected and we have to unselect it.
                        return prevCheckedItemsIDs.filter((itemID) => itemID !== reportItem.id)
                    }
                    // Report was not selected yet and we have to select it.
                    return [...prevCheckedItemsIDs, reportItem.id]
                })
            }
            return new Map(pPrevStateReportsAsMap)
        })
    }

    /**
     * Callback when mouse entered the report list item (report or folder) element.
     * @param rptListItemID
     */
    const handleMouseEnteredReportListItem = (rptListItemID: string): void => {
        setHoveredItemID(rptListItemID)
    }

    /**
     * Callback when mouse left the report list item (report or folder) element.
     * @param rptListItemID
     */
    const handleMouseLeftReportListItem = (rptListItemID: string): void => {
        setHoveredItemID((previousID) => (previousID === rptListItemID ? null : previousID))
    }

    /**
     * Report list item (report or folder) edit icon click handler.
     * @param event Click event.
     * @param rptListItemID
     */
    const handleOnEditReportListItemNameClick = (event: React.MouseEvent, rptListItemID: string): void => {
        event.stopPropagation()
        const reportListItemToEdit = reportListItemsAsMap.get(rptListItemID)
        if (reportListItemToEdit) {
            setEditingItemID(rptListItemID)
            setEditedName(reportListItemToEdit.name)
        }
    }

    /**
     * Handler of finished update of folder name. Designed to be used when user clicks outside the item
     * when editing or when hits enter.
     * @param rptListItemID Actually the folder identifier.
     */
    const handleFolderNameUpdated = async (rptListItemID: string): Promise<void> => {
        setEditingItemID(null)
        const folder = reportListItemsAsMap.get(rptListItemID)

        if (folder && folder.name !== editedName && !!editedName) {
            const folderIDToChange = folder.id

            try {
                // Remove the old name from the path.
                const pathWithoutName = folder.description.substring(0, folder.description.length - folder.name.length)
                // Build new path.
                const newPath = pathWithoutName + editedName
                setReportListItemsAsMap((pPrevStateReportsAsMap) => {
                    // We change the name to show immediately the optimistic update of the name.
                    folder.name = editedName

                    setEditedName('')
                    return new Map(pPrevStateReportsAsMap)
                })
                await renameFolder(servletPath, folder.description, newPath)

                // Add expanded folder ID in case it was expanded before the change and als update all item IDs that
                // are affected by the folder ID change.
                const checkedItemsIDsNew: string[] = []
                if (checkedItemsIDs.includes(folderIDToChange)) {
                    checkedItemsIDsNew.push(newPath)
                }
                checkedItemsIDs.forEach((ID) => {
                    if (ID.startsWith(`${folderIDToChange}${FOLDERS_SEPARATOR_BACKEND}`)) {
                        checkedItemsIDsNew.push(ID.replace(folderIDToChange, newPath))
                    } else {
                        checkedItemsIDsNew.push(ID)
                    }
                })
                setCheckedItemsIDs(checkedItemsIDsNew)

                // Temporary update all items that are related to the folder ID change to avoid the visual changes
                // in the menu during the request time and after the full update of the menu items when the response
                // is returned from the service. This logic have to "traverse" the items and reinsert them with correct
                // ID (updated after change) and als change their internal properties that are used in main menu logic
                // to build the menu tree correctly.
                const newMap: ReportListItemsIndexByReportID = new Map()
                Array.from(reportListItemsAsMap.entries()).forEach(([key, value]) => {
                    if (!value.isReport) {
                        // Update folders.
                        if (key === folderIDToChange) {
                            // Update for the folder that is currently being updated.
                            value.id = newPath
                            value.description = newPath
                            newMap.set(newPath, value)
                        } else if (key.startsWith(`${folderIDToChange}${FOLDERS_SEPARATOR_BACKEND}`)) {
                            // Update all the items that are children of the folder that is being updated.
                            value.id = key.replace(folderIDToChange, newPath)
                            value.description = key.replace(folderIDToChange, newPath)
                            value.parentFolderID = value.parentFolderID?.replace(folderIDToChange, newPath)
                            newMap.set(key.replace(folderIDToChange, newPath), value)
                        } else {
                            // Do not update non-related items.
                            newMap.set(key, value)
                        }
                    } else {
                        // Update reports.
                        if (value.parentFolderID === folderIDToChange) {
                            // Update parent folder ID of the folder tha is currently being updated.
                            value.parentFolderID = newPath
                        } else if (
                            value.parentFolderID?.startsWith(`${folderIDToChange}${FOLDERS_SEPARATOR_BACKEND}`)
                        ) {
                            // Update all the items that are children of the folder that is being updated.
                            value.parentFolderID = value.parentFolderID?.replace(folderIDToChange, newPath)
                        }
                        newMap.set(key, value)
                    }
                })
                setReportListItemsAsMap(newMap)

                // Reinitialise the list after successful change of folder name as all the items (reports) in the folder
                // should be changed as well because the report name is concatenated with folder name.
                initialiseReportListData()

                // Uncheck/collapse all obsolete items that were affected by folder ID change.
                setCheckedItemsIDs((prevCheckedItemsIDs) =>
                    prevCheckedItemsIDs.filter(
                        (ID) =>
                            ID !== folderIDToChange && !ID.startsWith(`${folderIDToChange}${FOLDERS_SEPARATOR_BACKEND}`)
                    )
                )
            } catch (error) {
                displayError(error as Error)

                // Fail-safely reinitialise the report list with correct names.
                initialiseReportListData()
            }
        }
    }

    /**
     * Handler of finished update of report name. Designed to be used when user clicks outside the item
     * when editing or when hits enter.
     * @param rptListItemID Actually the report identifier.
     */
    const handleReportNameUpdated = async (rptListItemID: string): Promise<void> => {
        setEditingItemID(null)
        const reportToEdit = reportListItemsAsMap.get(rptListItemID)

        if (reportToEdit && reportToEdit.name !== editedName && !!editedName) {
            // Remove the old name from description.
            const currentDescriptionWithoutName = reportToEdit.description.substring(
                0,
                reportToEdit.description.length - reportToEdit.name.length
            )
            // Build new description.
            const newDescription = currentDescriptionWithoutName + editedName

            try {
                setReportListItemsAsMap((pPrevStateReportsAsMap) => {
                    reportToEdit.description = newDescription
                    reportToEdit.name = editedName

                    setEditedName('')
                    return new Map(pPrevStateReportsAsMap)
                })

                await renameReport(servletPath, rptListItemID, newDescription)
                // Update report name in report portal if it is open
                handleReportUpdateRenameActiveRptDescription(rptListItemID, newDescription, undefined)
            } catch (error) {
                displayError(error as Error)

                // Fail-safely reinitialise the report list with correct names.
                initialiseReportListData()
            }
        }
    }

    /**
     * Handler for the "key down" event when editing the name of the folder or report.
     * Designed to be used to catch control keys (Enter, ESC).
     * @param event The keyboard event.
     * @param isFolder Whether the editing item is folder.
     */
    const handleKeyDownWhenNameEditing = (event: React.KeyboardEvent, isFolder: boolean): void => {
        switch (event.key) {
            case 'Enter':
                if (isFolder) {
                    handleFolderNameUpdated(id)
                } else {
                    handleReportNameUpdated(id)
                }
                break
            case 'Escape':
                setEditingItemID(null)
                setEditedName('')
                break
            default:
                break
        }
    }

    /**
     * This method fetches a local storage variable and decides if
     * a user has chosen to not show the prompt in the future.
     */
    const getBinPromptStatus = (): boolean => {
        const storedFlag = localStorage.getItem(NO_REMINDER_VARIABLE)
        return storedFlag === TRUE
    }

    /**
     * Prompt popup warning before moving folders to bin.
     */
    const displayPromptOnMoveToBinFolder = (reportIDs: ReportListItemType): void => {
        displayPrompt(
            <PromptMoveToBinMessage />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            (messageChecked?: boolean) => {
                handleFolderMoveToBin(reportIDs)
                // Set reminder prompt to no longer show.
                localStorage.setItem(NO_REMINDER_VARIABLE, (messageChecked ?? false).toString())
            },
            undefined,
            undefined,
            language.get(LanguageMyReportList.ReportlistMoveToBinCondition)
        )
    }

    /**
     * Prompt popup warning before moving reports to bin.
     */
    const displayPromptOnMoveToBinReport = (reportID: string): void => {
        displayPrompt(
            <PromptMoveToBinMessage />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            (messageChecked?: boolean) => {
                handleReportDelete(reportID)
                // Set reminder prompt to no longer show.
                localStorage.setItem(NO_REMINDER_VARIABLE, (messageChecked ?? false).toString())
            },
            undefined,
            undefined,
            language.get(LanguageMyReportList.ReportlistMoveToBinCondition)
        )
    }

    const onMouseOver = (): void => {
        setIsHighlighted(true)
        setHasHighlight(true)
    }
    const onMouseOut = (): void => {
        setIsHighlighted(false)
        setHasHighlight(false)
    }

    const onFolderActionsMouseOver = (): void => {
        setIsFolderHighlighted(true)
        setHasHighlight(true)
    }
    const onFolderActionsMouseOut = (): void => {
        setIsFolderHighlighted(false)
        setHasHighlight(false)
    }

    const {
        attributes,
        listeners,
        setNodeRef: draggableRef,
        transform,
        isDragging
    } = useDraggable({
        id: `${ReportDragDropIDs.DRAGGABLE_REPORT}|${id}`,
        data: {
            name,
            id,
            parentFolderID
        } as DraggableReportData,
        disabled: editingItemID !== null || dndDisabled
    })

    const DraggableStyles = {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 3000 : 1
    }

    const {
        attributes: folderAttributes,
        listeners: folderListenders,
        setNodeRef: draggableRefFolder,
        transform: folderTransform,
        isDragging: folderIsDragging
    } = useDraggable({
        id: `${ReportDragDropIDs.DRAGGABLE_FOLDER}|${id}`,
        data: {
            name,
            id,
            subItems,
            parentFolderID,
            reportListItem
        } as DraggableFolderData,
        disabled: editingItemID !== null || dndDisabled
    })

    const DraggableFolderStyles = {
        transform: CSS.Translate.toString(folderTransform),
        zIndex: folderIsDragging ? 3000 : 1
    }

    const { isOver, setNodeRef: droppableRef } = useDroppable({
        id: `${ReportDragDropIDs.DROPPABLE_FOLDER}|${id}`,
        data: {
            id,
            parentFolderID
        } as DroppableData,
        disabled: folderIsDragging || isDragging || editingItemID !== null || dndDisabled
    })

    useEffect(() => {
        if (isChecked && folderIsDragging) {
            setCheckedItemsIDs((prevCheckedItemIDs) =>
                prevCheckedItemIDs.filter((prevCheckedItemID) => prevCheckedItemID !== id)
            )
        }
    }, [folderIsDragging])

    return !!subItems && subItems.length > 0 ? (
        <div ref={draggableRefFolder} {...folderListenders} {...folderAttributes} style={DraggableFolderStyles}>
            <div
                ref={droppableRef}
                className={`report-list-wrapper ${
                    isFolderHighlighted || isParentFolderHighlighted ? 'highlighted-item' : ''
                }`}
            >
                <Accordion
                    key={`${name}-${id}`}
                    expanded={isChecked}
                    className={`${isOver ? ReportDragDropClasses.DND_CONTENT : ''}`}
                    onChange={(event): void => handleReportListFolderClick(event, id)}
                    TransitionProps={{
                        onEnter: (): void => setIsShowContentUntilTransitionFinishes(true),
                        onExited: (): void => setIsShowContentUntilTransitionFinishes(false)
                    }}
                >
                    <AccordionSummary
                        className={`summary ${
                            isFolderHighlighted || isParentFolderHighlighted ? 'highlighted-item' : ''
                        } ${isOver ? ReportDragDropClasses.DND_HEADING : ''}`}
                        expandIcon={isNameEditing ? <React.Fragment></React.Fragment> : <ExpandMoreIcon />}
                        IconButtonProps={{ disableRipple: true, disableFocusRipple: true }}
                        aria-controls='report-list-item-expansion-panel-summary-content'
                        id='report-list-item-expansion-panel-summary'
                        onMouseEnter={
                            isReportWriteAllowed ? (): void => handleMouseEnteredReportListItem(id) : undefined
                        }
                        onMouseLeave={isReportWriteAllowed ? (): void => handleMouseLeftReportListItem(id) : undefined}
                    >
                        {isNameEditing ? (
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            <ClickAwayListener onClickAway={async (): Promise<void> => handleFolderNameUpdated(id)}>
                                <div className='edit-name-wrapper'>
                                    <TextInput
                                        autoFocus
                                        onClick={(event): void => {
                                            event.stopPropagation()
                                        }}
                                        onKeyDown={(event): void => {
                                            handleKeyDownWhenNameEditing(event, true)
                                        }}
                                        value={editedName}
                                        onChange={setEditedName}
                                    />
                                </div>
                            </ClickAwayListener>
                        ) : (
                            <div>
                                {name}
                                {isReportWriteAllowed && isHovered && (
                                    <ContextMenu
                                        className='report-list-context-menu'
                                        menuItems={[
                                            {
                                                itemName: language.get(
                                                    LanguageMyReportList.ReportlistDeletereportsinfolder
                                                ),
                                                isEnabled: true, // Bin is Not Empty
                                                icon: <Icon iconName={IconName.DELETE} />,
                                                onClick: (): void => {
                                                    if (getBinPromptStatus()) {
                                                        handleFolderMoveToBin(reportListItem)
                                                        onFolderActionsMouseOut()
                                                    } else {
                                                        displayPromptOnMoveToBinFolder(reportListItem)
                                                    }
                                                },
                                                onMouseOver: onFolderActionsMouseOver,
                                                onMouseOut: onFolderActionsMouseOut
                                            },
                                            {
                                                itemName: language.get(LanguageMyReportList.ReportlistRenamefolder),
                                                isEnabled: true, // Bin is Not Empty
                                                icon: <Icon iconName={IconName.PENCIL} />,
                                                onClick: (event): void => {
                                                    handleOnEditReportListItemNameClick(event, id)
                                                    onFolderActionsMouseOut()
                                                }
                                            }
                                        ]}
                                        position={ContextMenuPosition.TOP_RIGHT}
                                        anchorElement={
                                            <div className='edit-icon-wrapper'>
                                                <IconButton
                                                    type={IconButtonType.BUTTON}
                                                    iconName={IconName.MORE_HORIZONTAL}
                                                    title={language.get(LanguageMyReportList.ReportlistMoreoptions)}
                                                />
                                            </div>
                                        }
                                    />
                                )}
                            </div>
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                        {(isFolderOpen || isShowContentUntilTransitionFinishes) && (
                            <div
                                className={`report-list-wrapper ${
                                    isFolderHighlighted || isParentFolderHighlighted ? 'highlighted-item' : ''
                                }`}
                            >
                                {subItems.map(
                                    (rptListItem): ReactNode => (
                                        <ReportListItem
                                            key={rptListItem.id}
                                            reportListItem={rptListItem}
                                            {...propagatedValues}
                                            isParentFolderHighlighted={isParentFolderHighlighted || isFolderHighlighted}
                                            dndDisabled={dndDisabled}
                                        />
                                    )
                                )}
                            </div>
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
        </div>
    ) : (
        <div
            ref={draggableRef}
            {...listeners}
            {...attributes}
            style={DraggableStyles}
            onMouseEnter={isReportWriteAllowed ? (): void => handleMouseEnteredReportListItem(id) : undefined}
            onMouseLeave={isReportWriteAllowed ? (): void => handleMouseLeftReportListItem(id) : undefined}
            className={`report-list-file ${isChecked ? 'rlf-selected' : ''} ${isHighlighted ? 'highlight-row' : ''}`}
        >
            {isNameEditing ? (
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                <ClickAwayListener onClickAway={async (): Promise<void> => handleReportNameUpdated(id)}>
                    <div className='edit-name-wrapper'>
                        <TextInput
                            autoFocus
                            onClick={(event): void => {
                                event.stopPropagation()
                            }}
                            onKeyDown={(event): void => {
                                handleKeyDownWhenNameEditing(event, false)
                            }}
                            value={editedName}
                            onChange={setEditedName}
                        />
                    </div>
                </ClickAwayListener>
            ) : (
                <>
                    {isReportWriteAllowed && (
                        <Checkbox
                            hideByDefault={true}
                            parentHover={isHovered}
                            onChange={(checked: boolean): void => handleReportListCheckboxClick(checked, id)}
                            isChecked={isChecked}
                        />
                    )}
                    <div
                        onClick={(): void => handleReportListReportClick(reportListItem)}
                        className='report-name'
                        title={durationFilterSummary}
                    >
                        <div className='schedule-name-wrapper'>
                            {name}
                            {isScheduleActive && (
                                <div className='schedule'>
                                    <Icon iconName={IconName.EMAIL} />
                                </div>
                            )}
                        </div>
                    </div>
                    {isReportWriteAllowed && isHovered && (
                        <ContextMenu
                            className='report-list-context-menu'
                            menuItems={[
                                {
                                    itemName: language.get(LanguageMyReportList.ReportlistDeletereport),
                                    isEnabled: isReportWriteAllowed,
                                    icon: <Icon iconName={IconName.DELETE} />,
                                    onClick: (): void => {
                                        if (getBinPromptStatus()) {
                                            handleReportDelete(id)
                                        } else {
                                            displayPromptOnMoveToBinReport(id)
                                        }
                                        onMouseOut()
                                    },
                                    onMouseOver,
                                    onMouseOut
                                },
                                {
                                    itemName: language.get(LanguageMyReportList.ReportlistCopyreport),
                                    isEnabled: isReportWriteAllowed,
                                    icon: <Icon iconName={IconName.COPY} />,
                                    onClick: (): void => {
                                        if (copyReport) {
                                            copyReport(id)
                                        }

                                        onMouseOut()
                                    },
                                    onMouseOver,
                                    onMouseOut
                                },
                                {
                                    itemName: language.get(LanguageMyReportList.ReportlistRenamereport),
                                    isEnabled: isReportWriteAllowed,
                                    icon: <Icon iconName={IconName.PENCIL} />,
                                    onClick: (e: React.MouseEvent<Element, MouseEvent>): void => {
                                        handleOnEditReportListItemNameClick(e, id)
                                        onMouseOut()
                                    },
                                    onMouseOver,
                                    onMouseOut
                                },
                                {
                                    itemName: language.get(LanguageMyReportList.ReportlistModifyreport),
                                    isEnabled: isReportWriteAllowed,
                                    icon: <Icon iconName={IconName.CONFIGURE} />,
                                    onClick: (e: React.MouseEvent): void => {
                                        if (modifyReport) {
                                            modifyReport(e, id)
                                        }

                                        onMouseOut()
                                    },
                                    onMouseOver,
                                    onMouseOut
                                }
                            ]}
                            position={ContextMenuPosition.TOP_RIGHT}
                            anchorElement={
                                <div className='edit-icon-wrapper'>
                                    <IconButton
                                        type={IconButtonType.BUTTON}
                                        iconName={IconName.MORE_HORIZONTAL}
                                        title={language.get(LanguageMyReportList.ReportlistMoreoptions)}
                                    />
                                </div>
                            }
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default React.memo(ReportListItem)
